import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ERRORS_MESSAGE } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { getCloneApplication } from "@/store/slices/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { getErrorToastPayload } from "@/utils/commonFunctions";
import type { IDispatchResponse } from "@/utils/interface";

type CloneApplicationResponse = IDispatchResponse & {
  payload: IDispatchResponse["payload"] & { data?: { _id?: string } };
};

/**
 * Clones an application and routes to the new copy, surfacing the API error message on failure.
 * Shared by the applications list and the application detail page.
 */
const useCloneApplication = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cloneApplication = useCallback(
    async (applicationId: string): Promise<void> => {
      const res = (await dispatch(getCloneApplication(applicationId))) as CloneApplicationResponse;

      if (res?.error) {
        dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
        return;
      }

      const clonedApplicationId = res?.payload?.data?._id;
      if (!clonedApplicationId) {
        // Fulfilled but with no id to route to - the response body's message would be a success
        // string, so fall back to the generic error rather than showing it in a danger snackbar.
        dispatch(openSnackbar({ message: ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }));
        return;
      }

      navigate(`/application/${clonedApplicationId}`);
    },
    [dispatch, navigate],
  );

  return { cloneApplication };
};

export { useCloneApplication };
